import { useEffect, useState } from "react";
import api from "../../lib/api";
import { inr } from "../../lib/format";
import { Info, Warning, ShieldCheck } from "@phosphor-icons/react";

/**
 * AvmLiteBanner — small banner that shows the city × property-type benchmark
 * range below the CMV (Current Market Value) field in the Add/Edit Property
 * form. Color-tiered confidence badge (High / Medium / Low) + above/below
 * market verdicts.
 *
 * Props:
 *   city, propertyType, cmv, carpetAreaSqft
 */
export default function AvmLiteBanner({ city, propertyType, cmv, carpetAreaSqft }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!city || !propertyType || !cmv) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    const ctrl = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          city, property_type: propertyType, cmv: String(cmv),
        });
        if (carpetAreaSqft) params.set("carpet_area_sqft", String(carpetAreaSqft));
        const { data } = await api.get(`/networth/cmv-check?${params.toString()}`);
        if (!cancelled) setData(data);
      } catch { if (!cancelled) setData({ available: false }); }
      finally { if (!cancelled) setLoading(false); }
    }, 350);   // debounce
    return () => { cancelled = true; clearTimeout(ctrl); };
  }, [city, propertyType, cmv, carpetAreaSqft]);

  if (!city || !cmv) return null;
  if (loading) return (
    <div className="text-[11px] text-muted-foreground mt-1" data-testid="avm-loading">
      Looking up market benchmark…
    </div>
  );
  if (!data?.available) return null;

  const verdict = data.verdict;
  const tone =
    verdict === "above_market" ? "bg-amber-500/10 border-amber-500/40 text-amber-700" :
    verdict === "below_market" ? "bg-blue-500/10 border-blue-500/40 text-blue-700" :
    verdict === "within_range" ? "bg-teal-500/10 border-teal-500/40 text-teal-700" :
    "bg-muted/30 border hairline";
  const Icon =
    verdict === "above_market" ? Warning :
    verdict === "within_range" ? ShieldCheck : Info;

  const conf = data.confidence;
  const confTone =
    conf === "High"   ? "bg-green-500/15 text-green-700" :
    conf === "Medium" ? "bg-amber-500/15 text-amber-700" :
    conf === "Low"    ? "bg-red-500/15 text-red-700" : "bg-muted/30";

  return (
    <div className={`border px-2.5 py-2 mt-2 text-[11px] ${tone}`} data-testid="avm-banner">
      <div className="flex items-start gap-2">
        <Icon size={14} weight="duotone" className="mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div>
            Market benchmark for <b>{data.city} {data.property_type}</b>:
            ₹{data.price_per_sqft_low.toLocaleString("en-IN")} – ₹{data.price_per_sqft_high.toLocaleString("en-IN")} / sqft
            {data.suggested_low && (
              <span className="block mt-0.5">
                ≈ {inr(data.suggested_low)} – {inr(data.suggested_high)} total (at {carpetAreaSqft} sqft carpet)
              </span>
            )}
          </div>
          {verdict === "above_market" && (
            <div className="font-medium mt-1">Your estimate is above the market range. Please verify.</div>
          )}
          {verdict === "below_market" && (
            <div className="font-medium mt-1">Your estimate appears conservative vs market range.</div>
          )}
          {verdict === "within_range" && (
            <div className="font-medium mt-1">Within market range.</div>
          )}
          <div className="text-[10px] text-muted-foreground mt-1">
            Source: {data.source} · updated {data.updated_month}
          </div>
        </div>
        {conf && (
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${confTone}`}
            data-testid="avm-confidence-badge">
            {conf}
          </span>
        )}
      </div>
    </div>
  );
}
